/**
 * Shared modal shell — backdrop + container chrome, so every modal has the
 * same backdrop opacity, radius, border, and mobile bottom-sheet behavior.
 * (Modals were previously hand-rolled per feature with drifting backdrop
 * opacity — bg-black/60 vs /70 — and inconsistent containers.)
 *
 * Click-outside + the optional close button both dismiss. Use for any local/
 * inline modal; the auto-firing global modals (Upgrade/Welcome/…) keep their
 * Providers but can render their content inside this shell.
 */

import type { ReactNode } from "react";

import { X } from "@/shared/components/icons";

export function ModalOverlay({
  title,
  onClose,
  children,
  showClose = true,
}: {
  title?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  showClose?: boolean;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-border bg-surface p-5"
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showClose) && (
          <div className="flex items-start justify-between gap-3 mb-4">
            {title ? (
              <h2 className="text-lg font-bold text-text">{title}</h2>
            ) : (
              <span />
            )}
            {showClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="-mr-1 -mt-1 rounded-lg p-1 text-text-muted hover:text-text transition"
              >
                <X size={18} strokeWidth={1.75} />
              </button>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
